import * as React from 'react';

import FormRow from '../../../../../components/FormRow';
import InputHandler from '../../../../../components/InputHandler';
import QuestionStepper from '../../../../../components/QuestionStepper';
import FormSection from '../../../../../components/FormSection';

import { styled } from '@mui/system';

import { steps as chopINTENDSteps, getStepContent as getCHOPINTENDSteps, steps } from '../chopINTENDSettings';
import { getPatientAgeInMonthsWithOneDecimal } from '../../../../../utility/patientInfo';
import { chopINTENDScore, showPatientAgeInMonthsOnForm } from '../../../utils';
import InfoPopover from '../../../../../components/InfoPopover';
import colors from '../../../../../config/theme/colors';
import { useIntl } from 'react-intl';
import { Container, Item } from 'Components/Grid';
import SimpleList from 'Components/SimpleList';
import { getMissingFields } from 'Utility/documentHandling';

const Text = styled('div')({
  color: colors.tertiaryText,
});

const testingEnvironmentFields = ['ideally', 'paddedMat', 'diaper', 'redBall', 'pacifier', 'cnt'];

const CHOPINTENDForm = ({ formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const mainSteps = steps.map(s => Object.keys(s)).flat();
  return (
    <React.Fragment>
      <FormRow title="motor.labels.chopINTEND.testingEnvironment.title">
        <Container>
          <Item>
            <Text>
              <React.Fragment>
                {testingEnvironmentFields.map(
                  (field: string) => (
                    <React.Fragment key={field}>
                      {fm(`motor.labels.chopINTEND.testingEnvironment.${field}`)}
                      <br />
                    </React.Fragment>
                  ),
                  testingEnvironmentFields,
                )}
              </React.Fragment>
            </Text>
          </Item>
        </Container>
      </FormRow>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="general.timeOfDay" id={'time'}>
        <InputHandler type="TimePicker" editing={!viewing} name="time" formData={formData} timeDefault="now" />
      </FormRow>
      <FormRow title="motor.hoursSinceLastFeeding">
        <InputHandler
          type="NumberField"
          editing={!viewing}
          name="hoursSinceLastFeeding"
          formData={formData}
          placeholder={'motor.hoursSinceLastFeedingShort'}
          min={0}
        />
      </FormRow>
      <FormRow title="motor.ageMonth" condition={showPatientAgeInMonthsOnForm(formData.document.date)}>
        <InputHandler
          type="TextField"
          editing={false}
          name="age"
          formData={{
            onChange: formData.onChange,
            document: {
              age: formData.document.date ? getPatientAgeInMonthsWithOneDecimal(formData.document.date) : '',
            },
          }}
          disabled={true}
        />
      </FormRow>
      <FormSection>
        <QuestionStepper
          viewing={viewing}
          formData={formData}
          pageType={'motor'}
          docType={'chopINTEND'}
          steps={chopINTENDSteps}
          getStepContent={getCHOPINTENDSteps}
        />
      </FormSection>
      <FormSection>
        <FormRow
          title={
            chopINTENDScore(formData.document) === formData.document.manualScore &&
            formData.document.manualScore != null
              ? 'motor.manuallyGivenScore'
              : 'motor.automaticScore'
          }
        >
          <Container style={{ fontSize: '1.8rem', fontWeight: 600, color: colors.primary }}>
            {chopINTENDScore(formData.document) === 'notCounted' ? (
              <InfoPopover
                text={
                  <SimpleList
                    title={fm('general.couldntCalculate')}
                    listItems={getMissingFields(steps.map(s => Object.values(s)).flat(2), formData.document).map(
                      f =>
                        `${mainSteps.findIndex(step => f.includes(step)) + 1}. ${fm(`motor.labels.chopINTEND.${f}`)}`,
                    )}
                  />
                }
                color="primary"
              />
            ) : (
              chopINTENDScore(formData.document)
            )}
          </Container>
        </FormRow>
        <FormRow title="motor.manualScore">
          <Container style={{ fontSize: '1.8rem', fontWeight: 600, color: colors.primary }}>
            <InputHandler
              type="NumberField"
              editing={!viewing}
              name="manualScore"
              formData={formData}
              width={4}
              height={3}
              maxLength={2}
            />
          </Container>
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<ICHOPINTEND>;
  viewing: boolean;
}

export default CHOPINTENDForm;
